import { safeWindow } from '~/bridge/util/safeWindow'

/**
 * Global configuration object for the Notes app
 */
interface NotesApiConfig {
  auth: {
    audience: string
    clientId: string
    domain: string
  }
  editorApi: {
    baseUrl: string
  }
  meshingApi: {
    baseUrl: string
  }
}

class NotesApiConfigError extends Error {
  /**
   * Error to be thrown when the app config is invalid in some way (e.g. missing required values)
   */
  constructor(message: string) {
    super(message)
    this.name = 'NotesApiConfigError'
  }
}

const buildBaseUrl = (
  configBaseUrl: string | undefined,
  configPortFallback: string | undefined,
): string => {
  /**
   * Build a baseUrl for an API client.
   *
   * - If a base URL is explicitly provided, use that.
   * - Otherwise, build a base URL relative to the current protocol/domain with the specified port.
   * (This is only useful for local development, where the API server is exposed on a specific port on localhost.)
   */
  if (configBaseUrl) {
    return configBaseUrl
  }
  if (configPortFallback) {
    return `${safeWindow?.location.protocol}//${safeWindow?.location.hostname}:${configPortFallback}`
  }
  if (process.env.NODE_ENV === 'test') {
    return ''
  }
  throw new NotesApiConfigError('No base URL or port fallback provided')
}

const buildConfig = (): NotesApiConfig => ({
  auth: {
    domain: '',
    clientId: '',
    audience: '',
  },
  editorApi: {
    baseUrl: buildBaseUrl(
      import.meta.env.VITE_EDITOR_API_BASE_URL,
      import.meta.env.VITE_NEXT_PUBLIC_EDITOR_API_PORT,
    ),
  },
  meshingApi: {
    baseUrl: buildBaseUrl(
      import.meta.env.VITE_MESHING_API_BASE_URL,
      import.meta.env.VITE_NEXT_PUBLIC_MESHING_API_PORT,
    ),
  },
})

export const config = buildConfig()
